/**
 * Filtro de anúncios por marca e, no Mercado Livre, a ação para marca proibida na conta:
 * bloquear a marca (estoque 0 e não publica mais) e excluir todos os anúncios dela.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Trash2 } from 'lucide-react';
import { api, int } from '../api.ts';
import { ErrorBox, Tag } from './ui.tsx';

interface BrandRow { brand: string; total: number; active: number; blocked: boolean }

export function useListingBrands(channel: string, accountId?: string) {
  return useQuery({
    queryKey: ['listing-brands', channel, accountId ?? null],
    queryFn: () => api<BrandRow[]>(`/channels/listings/brands?${new URLSearchParams({ channel, ...(accountId ? { account: accountId } : {}) })}`),
    staleTime: 60_000,
  });
}

/** Campo de marca com sugestões (as marcas dos produtos anunciados). */
export function BrandFilterInput({ channel, accountId, params, set }: {
  channel: string; accountId?: string; params: URLSearchParams; set: (p: Record<string, string | null>) => void;
}) {
  const brands = useListingBrands(channel, accountId);
  const [text, setText] = useState(params.get('brand') ?? '');
  useEffect(() => setText(params.get('brand') ?? ''), [params.get('brand')]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = (v: string) => {
    const t = v.trim();
    if (t !== (params.get('brand') ?? '')) set({ brand: t || null, page: null });
  };
  const listId = `brands-${channel}`;
  return (
    <>
      <input className="input" style={{ maxWidth: 200 }} list={listId} placeholder="Marca" aria-label="Marca" value={text}
        onChange={(e) => {
          setText(e.target.value);
          // Escolheu da lista: aplica na hora.
          if (brands.data?.some((b) => b.brand === e.target.value)) apply(e.target.value);
        }}
        onBlur={() => apply(text)} onKeyDown={(e) => { if (e.key === 'Enter') apply(text); }} />
      <datalist id={listId}>
        {brands.data?.map((b) => <option key={b.brand} value={b.brand}>{`${int(b.total)} anúncio(s)${b.blocked ? ' · bloqueada' : ''}`}</option>)}
      </datalist>
    </>
  );
}

/** Barra da marca filtrada (Mercado Livre): quantos anúncios, bloquear e excluir todos. */
export function BrandActionBar({ accountId, brand, canEdit, onDone }: {
  accountId: string; brand: string; canEdit: boolean; onDone: () => void;
}) {
  const qc = useQueryClient();
  // Todos os anúncios da marca (sem os outros filtros da tela): é o que a exclusão alcança.
  const count = useQuery({
    queryKey: ['meli', 'brand-count', brand],
    queryFn: () => api<{ total: number }>(`/channels/mercadolivre/listings?${new URLSearchParams({ brand, pageSize: '1' })}`),
  });
  const total = count.data?.total ?? 0;
  const brands = useListingBrands('mercadolivre', accountId);
  const blocked = brands.data?.find((b) => b.brand.toLowerCase() === brand.toLowerCase())?.blocked ?? false;
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  useEffect(() => { setConfirming(false); setTyped(''); }, [brand]);
  const act = useMutation({
    mutationFn: (body: { block: boolean; deleteListings: boolean }) =>
      api<{ blocked: boolean; queued: number }>(`/channels/accounts/${accountId}/brand-action`, { method: 'POST', json: { brand, ...body } }),
    onSuccess: () => {
      setConfirming(false); setTyped('');
      qc.invalidateQueries({ queryKey: ['listing-brands'] });
      qc.invalidateQueries({ queryKey: ['meli'] });
      onDone();
    },
  });
  return (
    <div className={`notice ${blocked ? 'warn' : ''}`} style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
      <span><b>{int(total)}</b> anúncio{total === 1 ? '' : 's'} da marca <b>{brand}</b> nesta conta</span>
      {blocked && <Tag tone="warn">Marca bloqueada: não publica e vai com estoque 0</Tag>}
      <span className="grow" style={{ flex: 1 }} />
      {canEdit && !blocked && (
        <button className="btn sm" type="button" disabled={act.isPending} onClick={() => act.mutate({ block: true, deleteListings: false })}
          title="A marca não pode mais ser publicada nesta conta e os anúncios existentes vão com estoque 0, até você liberar em Configurações do canal › Bloqueios.">
          <Ban size={14} /> Bloquear a marca
        </button>
      )}
      {canEdit && total > 0 && !confirming && (
        <button className="btn sm danger" type="button" disabled={act.isPending} onClick={() => setConfirming(true)}>
          <Trash2 size={14} /> Excluir os {int(total)} anúncios{blocked ? '' : ' e bloquear'}
        </button>
      )}
      {confirming && (
        <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span className="small">
            Os anúncios são finalizados e <b>excluídos no Mercado Livre</b> (não dá para desfazer) e a marca fica bloqueada nesta conta.
            Para confirmar, digite <b>{brand}</b>:
          </span>
          <input className="input" style={{ maxWidth: 200 }} value={typed} onChange={(e) => setTyped(e.target.value)} aria-label="Confirme a marca" autoFocus />
          <button className="btn sm danger" type="button" disabled={typed.trim().toLowerCase() !== brand.toLowerCase() || act.isPending}
            onClick={() => act.mutate({ block: true, deleteListings: true })}>
            {act.isPending ? 'Enviando…' : 'Excluir e bloquear'}
          </button>
          <button className="btn sm" type="button" onClick={() => { setConfirming(false); setTyped(''); }}>Cancelar</button>
        </div>
      )}
      {act.data && (
        <span className="small" style={{ flexBasis: '100%' }}>
          {act.data.queued > 0
            ? `${int(act.data.queued)} anúncio(s) sendo excluídos no Mercado Livre em segundo plano (alguns minutos). Atualize a lista para acompanhar.`
            : 'Marca bloqueada nesta conta.'}
        </span>
      )}
      {act.error && <div style={{ flexBasis: '100%' }}><ErrorBox error={act.error} /></div>}
    </div>
  );
}
